import React, { forwardRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Newsletter = forwardRef(function Newsletter(_props, ref) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const openDisclaimer = (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setShowDisclaimer(true);
  };

  const confirmSubscribe = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/subscribers`, {
        email,
        name: name || null,
        source: "newsletter-section",
        consent: true,
      });
      if (res.data?.status === "already_subscribed") {
        toast("You're already on the list.", { description: "We'll write soon." });
      } else {
        toast.success("You're on the list.", {
          description: "Look out for a gentle note from Books for Little Hands.",
        });
      }
      setEmail("");
      setName("");
      setShowDisclaimer(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="newsletter"
      ref={ref}
      className="section relative"
      style={{ background: "var(--ink)", color: "var(--paper)" }}
      data-testid="newsletter-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-14 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1 }}
          className="col-span-12 md:col-span-6"
        >
          <p className="folio-num" style={{ color: "rgba(249,248,243,0.55)" }}>
            Correspondence · Join the list
          </p>
          <h2 className="mt-4 font-serif text-5xl md:text-7xl leading-[0.9] tracking-tight text-[color:var(--paper)]">
            Slow letters from <em className="italic" style={{ color: "var(--accent-yellow)" }}>a small publishing table.</em>
          </h2>
          <p className="mt-6 text-lg leading-relaxed max-w-lg" style={{ color: "rgba(249,248,243,0.75)" }}>
            One thoughtful email when a new book is ready. Occasional printable activities.
            Never anything loud. Unsubscribe with a single click.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, delay: 0.1 }}
          onSubmit={openDisclaimer}
          className="col-span-12 md:col-span-6 md:pt-4"
          data-testid="newsletter-form"
        >
          <label className="folio-num block" style={{ color: "rgba(249,248,243,0.55)" }}>
            Your name (optional)
          </label>
          <input
            type="text"
            className="line-input mt-3"
            style={{ color: "var(--paper)", borderBottomColor: "rgba(249,248,243,0.35)" }}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Little reader's parent"
            data-testid="newsletter-name-input"
          />

          <label className="folio-num block mt-10" style={{ color: "rgba(249,248,243,0.55)" }}>
            Email address
          </label>
          <input
            type="email"
            required
            className="line-input mt-3"
            style={{ color: "var(--paper)", borderBottomColor: "rgba(249,248,243,0.35)" }}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="newsletter-email-input"
          />

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              type="submit"
              className="btn-solid"
              style={{
                background: "var(--paper)",
                color: "var(--ink)",
                borderColor: "var(--paper)",
              }}
              data-testid="newsletter-submit-button"
            >
              Sign me up
              <span>→</span>
            </button>
            <p className="text-xs uppercase tracking-[0.25em]" style={{ color: "rgba(249,248,243,0.5)" }}>
              A disclaimer will appear before submitting.
            </p>
          </div>
        </motion.form>
      </div>

      <Dialog open={showDisclaimer} onOpenChange={setShowDisclaimer}>
        <DialogContent
          className="max-w-[520px] rounded-none border border-stone-900/20 bg-[color:var(--paper)] text-[color:var(--ink)] p-8"
          data-testid="disclaimer-dialog"
        >
          <DialogHeader className="text-left space-y-3">
            <p className="folio-num">Before you sign, please read</p>
            <DialogTitle className="font-serif text-3xl md:text-4xl leading-tight tracking-tight">
              A small note on your email.
            </DialogTitle>
            <DialogDescription className="text-stone-700 text-base leading-relaxed pt-2">
              By signing up, you agree that your email address will be shared with the creators of
              <em> Books for Little Hands</em> so they can send occasional updates about new books,
              printable activities, and gentle reading notes.
              <br />
              <br />
              We never sell your email. We never share it with third parties beyond the creators of
              this book series. You can unsubscribe at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex flex-row items-center gap-3">
            <button
              type="button"
              className="btn-outline"
              onClick={() => setShowDisclaimer(false)}
              disabled={submitting}
              data-testid="disclaimer-cancel-button"
            >
              Not now
            </button>
            <button
              type="button"
              className="btn-solid"
              onClick={confirmSubscribe}
              disabled={submitting}
              data-testid="disclaimer-agree-button"
            >
              {submitting ? "Signing you up…" : "I agree, subscribe me"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
});

export default Newsletter;
